import React, { useEffect, useState } from 'react';
import { Box, Typography, Divider } from '@mui/material';
import FieldRenderer from './FieldRenderer';
import { PageTitle } from '../StyledComponents';

/**
 * ColumnLayout: Multi-column layout with percentage-based widths
 * Fields within each column stack vertically (unlike group which wraps horizontally)
 * 
 * Props:
 * - block: {
 *     type: 'columnLayout',
 *     columns: Array<{
 *       width: string, // e.g., '50%', '33.33%', '66.67%'
 *       fields: Array<field objects>
 *     }>
 *   }
 * - formData: object
 * - setFormData: function
 * - dynamicOptions: object
 * - dirtyMap: object
 * - setDirtyMap: function
 * - readOnly: boolean
 * 
 * Example:
 * {
 *   type: 'columnLayout',
 *   columns: [
 *     {
 *       width: '50%',
 *       fields: [
 *         { type: 'text', name: 'field1', label: 'Field 1' },
 *         { type: 'text', name: 'field2', label: 'Field 2' },
 *       ]
 *     },
 *     {
 *       width: '50%',
 *       fields: [
 *         { type: 'text', name: 'field3', label: 'Field 3' },
 *         { type: 'text', name: 'field4', label: 'Field 4' },
 *       ]
 *     }
 *   ]
 * }
 */
export default function ColumnLayout(props) {
  const columns = props.block?.columns || [];

  // Force a lightweight re-render when dynamicOptions changes so
  // nested Autocompletes receive updated options immediately.
  const [optionsNonce, setOptionsNonce] = useState(0);
  useEffect(() => {
    setOptionsNonce((n) => n + 1);
  }, [props.dynamicOptions]);

  if (!columns.length) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: 1.5,
        alignItems: 'flex-start',
        flexWrap: { xs: 'wrap', md: 'nowrap' },
      }}
    >
      {columns.map((column, colIdx) => (
        <Box
          key={colIdx}
          sx={{
            width: { xs: '100%', md: column.width || 'auto' },
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          {/* Optional per-column header */}
          {(column.label || column.header) && (
            <Box>
              <PageTitle gutterBottom>
                {column.label || column.header}
              </PageTitle>
              {column.divider !== false && <Divider sx={{ mt: 0.5, mb: 1 }} />}
            </Box>
          )}

          {column.fields?.map((field, fieldIdx) => (
            <Box key={`${field?.name || fieldIdx}-${optionsNonce}`}>
              {field?.type === 'group' && Array.isArray(field.fields) ? (
                <Box>
                  {field.label && (
                    <Typography variant="subtitle2" gutterBottom>
                      {field.label}
                    </Typography>
                  )}
                  <Box display="flex" flexWrap="wrap" gap={1} alignItems="flex-start">
                    {field.fields.map((gField, gIdx) => (
                      <Box key={`${gField.name || gIdx}-${optionsNonce}`} flex={gField.flex || '1 1 280px'} minWidth={240}>
                        <FieldRenderer
                          field={gField}
                          formData={props.formData}
                          setFormData={props.setFormData}
                          dynamicOptions={props.dynamicOptions}
                          dirtyMap={props.dirtyMap}
                          setDirtyMap={props.setDirtyMap}
                          readOnly={props.readOnly}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              ) : (
                <FieldRenderer
                  field={field}
                  formData={props.formData}
                  setFormData={props.setFormData}
                  dynamicOptions={props.dynamicOptions}
                  dirtyMap={props.dirtyMap}
                  setDirtyMap={props.setDirtyMap}
                  readOnly={props.readOnly}
                />
              )}
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}
